import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, Close } from '@mui/icons-material';
import './HomeHero.css';

const HomeHero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Keep video always muted
  const [isMuted] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Our Work', path: '/work' },
    { label: 'About Us', path: '/about' },
    { label: 'Link Up', path: '/contact' }
  ];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set initial mute state from saved preference
    video.muted = isMuted;
    video.volume = 1.0;

    // Set video volume and try to play
    const playVideo = async () => {
      try {
        // Always keep video muted for autoplay
        video.muted = true;
        
        await video.play();
      } catch (error) {
        console.log('Video autoplay error:', error);
        // Try again on user interaction
        const handleInteraction = () => {
          video.muted = true;
          video.play().catch(e => console.log('Play failed:', e));
          document.removeEventListener('click', handleInteraction);
          document.removeEventListener('touchstart', handleInteraction);
        };
        document.addEventListener('click', handleInteraction);
        document.addEventListener('touchstart', handleInteraction);
      }
    };

    // Preserve mute state when video loops (seeking/restarting)
    const handleSeeking = () => {
      // Always keep video muted when video seeks/loops
      video.muted = true;
    };
    
    const handleSeeked = () => {
      // Ensure mute state is maintained after seeking
      video.muted = true;
    };

    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);

    // Wait for video metadata to load
    if (video.readyState >= 2) {
      playVideo();
    } else {
      video.addEventListener('loadedmetadata', playVideo);
      video.addEventListener('canplay', playVideo);
    }

    return () => {
      video.removeEventListener('loadedmetadata', playVideo);
      video.removeEventListener('canplay', playVideo);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [isMuted]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Header inside Hero */}
      <header
        className={`fixed z-[100] transition-all duration-300 ${
          isScrolled
            ? 'top-3 left-3 right-3 rounded-full bg-white/15 backdrop-blur-md border border-white/25 shadow-lg'
            : 'top-0 left-0 right-0 rounded-none bg-transparent'
        }`}
      >
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-shrink-0"
            >
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo/New%20Bizani%20logo.png" 
                  alt="Bizani Logo" 
                  className="h-12 w-auto transition-all duration-300"
                  style={
                    !isScrolled
                      ? {
                          filter:
                            'brightness(0) saturate(100%) invert(37%) sepia(90%) saturate(1234%) hue-rotate(346deg) brightness(98%) contrast(92%)'
                        }
                      : {}
                  }
                />
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={item.path}
                    className={`px-4 py-2 text-sm font-ui font-semibold transition-colors duration-200 relative group ${
                      isScrolled ? 'text-white hover:text-bizani-orange' : 'text-white hover:text-bizani-orange'
                    }`}
                  >
                    {item.label}
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-bizani-orange transition-all duration-300 ${
                      location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}></span>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden p-2 transition-colors ${
                isScrolled ? 'text-white hover:text-bizani-orange' : 'text-white hover:text-bizani-orange'
              }`}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <Close className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation - full width & height overlay */}
          <motion.div
            initial={false}
            animate={{
              opacity: isMenuOpen ? 1 : 0,
              visibility: isMenuOpen ? 'visible' : 'hidden'
            }}
            transition={{ duration: 0.3 }}
            className={`lg:hidden fixed top-0 left-0 right-0 bottom-0 w-screen h-screen min-h-[100dvh] z-[90] ${isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{ width: '100vw', height: '100dvh', minHeight: '100dvh' }}
          >
            <div className="absolute top-0 left-0 right-0 bottom-0 w-full h-full flex flex-col justify-center bg-black/90" style={{ minHeight: '100dvh' }}>
              {/* Close X button - top right */}
              <button
                type="button"
                onClick={() => setIsMenuOpen(false)}
                className="absolute top-6 right-6 p-2 text-white hover:text-bizani-orange transition-colors z-10"
                aria-label="Close menu"
              >
                <Close className="w-8 h-8" />
              </button>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-6 py-4 text-lg font-ui font-semibold text-center text-white hover:bg-white/10 hover:text-bizani-orange transition-colors duration-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        </nav>
      </header>

      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ 
            width: '100%', 
            height: '100%', 
            minHeight: '100vh',
            objectFit: 'cover'
          }}
          onError={(e) => {
            console.error('Video error:', e);
            const video = e.currentTarget;
            console.error('Video error details:', video.error);
            console.error('Video src:', video.src);
          }}
          onLoadedData={() => {
            console.log('Video loaded and ready');
            if (videoRef.current) {
              videoRef.current.play().catch(e => console.log('Play error:', e));
            }
          }}
        >
          <source src="/videos/Bizani Media 2025 Showreel _ Digital Storytelling, Documentaries _ Development Content.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black/60 z-10"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 relative z-10 mt-24 sm:mt-32 md:mt-40">
        <div className="text-left max-w-4xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-bold mb-6 leading-tight text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl"
            style={{ 
              fontFamily: "'Montserrat', sans-serif",
              color: '#EF3F23'
            }}
          >
            DIGITAL STORYTELLING, STRATEGY AND INNOVATION
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white mb-8 max-w-3xl leading-relaxed"
            style={{ 
              fontFamily: "'Poppins', sans-serif",
              fontSize: '1.125rem',
              wordSpacing: '0.2em',
              letterSpacing: '0.05em'
            }}
          >
            We help organisations of all sizes to achieve their communication goals through captivating storytelling through our team of skilled media production professionals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 items-start"
          >
            <Link
              to="/contact"
              className="button-hover group relative inline-flex items-center px-6 py-3 rounded-lg font-headline font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
              style={{ 
                border: '2px solid #EF3F23',
                color: '#EF3F23',
                backgroundColor: 'transparent',
                fontSize: '0.875rem'
              }}
            >
              <span className="button-text">
                Get Started
              </span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-20"
      >
        <p
          className="text-white text-sm sm:text-base md:text-lg lg:text-xl"
          style={{ 
            fontFamily: "'HolidayFree', sans-serif",
            color: '#EF3F23',
            opacity: 0.7
          }}
        >
          documenting real life
        </p>
      </motion.div>
    </section>
  );
};

export default HomeHero;
